import React from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import Seo from "@/components/Seo";
import { ArrowRight, Zap, Package, Clock, BadgeCheck, Phone, Mail } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1645736315000-6f788915923b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwxfHxmb3JrbGlmdCUyMHdhcmVob3VzZXxlbnwwfHx8fDE3NzYxMzA4ODd8MA&ixlib=rb-4.1.0&q=85&w=1600";
const ABOUT_IMG = "https://images.unsplash.com/photo-1714627798569-b3e36d409c4b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwxfHxpbmR1c3RyaWFsJTIwZm9ya2xpZnQlMjBpc29sYXRlZHxlbnwwfHx8fDE3NzYxMzA4OTh8MA&ixlib=rb-4.1.0&q=85&w=800";
const HANGCHA_IMG = "https://customer-assets.emergentagent.com/job_5062d3ea-1248-4753-92e8-ff0b3e1f334b/artifacts/5q9o3xb1_Screenshot%202026-05-07%20140622.png";

const HANGCHA_TXT = {
  fr: {
    badge: "Distributeur officiel — Canada",
    title: "Chariots élévateurs HANGCHA",
    subtitle: "Une marque mondiale de confiance, maintenant à votre portée au Québec.",
    new_label: "Neuf",
    new_desc: "Distributeur officiel HANGCHA — modèles électriques, propane et diesel directement de l'usine.",
    used_label: "Usagé",
    used_desc: "Inventaire varié de chariots reconditionnés, inspectés et garantis.",
    cta: "Contactez-nous",
    cta_call: "Appeler",
  },
  en: {
    badge: "Official Distributor — Canada",
    title: "HANGCHA Forklifts",
    subtitle: "A trusted global brand, now within reach in Quebec.",
    new_label: "New",
    new_desc: "Official HANGCHA distributor — electric, propane, and diesel models direct from factory.",
    used_label: "Used",
    used_desc: "Varied inventory of refurbished, inspected and warrantied forklifts.",
    cta: "Contact us",
    cta_call: "Call",
  },
};

export default function LandingPage() {
  const { t, lang } = useLanguage();
  const h = HANGCHA_TXT[lang === "en" ? "en" : "fr"];

  const seoTitle = lang === "en"
    ? "Fork Finder — Forklifts in Quebec | Official HANGCHA Distributor"
    : "Fork Finder — Chariots élévateurs au Québec | Distributeur HANGCHA";
  const seoDesc = lang === "en"
    ? "Fast forklift service in Quebec. Official HANGCHA distributor — new and used, propane, diesel, and electric. We match your needs with available inventory."
    : "Service rapide de chariots élévateurs au Québec. Distributeur officiel HANGCHA — neuf et usagé, propane, diesel et électrique. Nous croisons vos besoins avec l'inventaire disponible.";

  const orgLd = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": `${typeof window !== "undefined" ? window.location.origin : ""}/#organization`,
    name: "Fork Finder",
    alternateName: "Gestion Farcar",
    url: typeof window !== "undefined" ? window.location.origin : undefined,
    logo: typeof window !== "undefined" ? `${window.location.origin}/favicon.ico` : undefined,
    image: HANGCHA_IMG,
    telephone: "+1-514-973-4754",
    email: "contact@forkfinder.ca",
    address: {
      "@type": "PostalAddress",
      addressRegion: "QC",
      addressCountry: "CA",
    },
    areaServed: { "@type": "AdministrativeArea", name: "Quebec" },
    description: seoDesc,
    sameAs: [],
  };

  return (
    <div data-testid="landing-page">
      <Seo
        title={seoTitle}
        description={seoDesc}
        canonicalPath="/"
        lang={lang}
        image={HERO_IMG}
        type="website"
        jsonLd={orgLd}
        alternates={{ fr: "/", en: "/?lang=en" }}
      />
      {/* Hero */}
      <section className="relative min-h-[90vh] flex items-center" data-testid="hero-section">
        <div className="absolute inset-0 z-0">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-white/90" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-32">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-gray-700 mb-6">
            {t.hero.overline}
          </p>
          <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl tracking-tight font-black text-gray-900 max-w-3xl leading-[1.05]">
            {t.hero.title}
          </h1>
          <p className="mt-6 text-base md:text-lg text-gray-700 max-w-xl leading-relaxed">
            {t.hero.subtitle}
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link
              to="/inventory"
              data-testid="hero-browse-btn"
              className="inline-flex items-center gap-2 bg-yellow-400 text-gray-900 font-bold hover:bg-yellow-500 transition-colors px-6 py-3 rounded-sm"
            >
              {t.hero.cta_browse} <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/request"
              data-testid="hero-request-btn"
              className="inline-flex items-center gap-2 bg-gray-100 text-gray-900 font-medium hover:bg-gray-200 transition-colors px-6 py-3 rounded-sm border border-gray-200"
            >
              {t.hero.cta_request}
            </Link>
          </div>
        </div>
      </section>

      {/* HANGCHA Official Distributor — featured section */}
      <section data-testid="hangcha-section" className="bg-white py-16 md:py-20 border-t border-gray-100">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          {/* Title block centered ABOVE the image */}
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 bg-yellow-50 border border-yellow-200 text-yellow-900 px-4 py-2 rounded-full text-xs font-bold mb-5 uppercase tracking-wider">
              <BadgeCheck className="w-4 h-4 text-yellow-600" />
              {h.badge}
            </div>
            <h2 className="font-heading text-4xl sm:text-5xl tracking-tight font-bold text-gray-900 mb-4">
              {h.title}
            </h2>
            <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
              {h.subtitle}
            </p>
          </div>

          {/* Hero image — full width, prominent */}
          <div className="relative overflow-hidden rounded-sm shadow-2xl ring-1 ring-gray-200 mb-10">
            <img
              src={HANGCHA_IMG}
              alt="HANGCHA forklift — Lithium Powered"
              className="w-full h-auto object-cover"
            />
          </div>

          {/* New + Used cards + CTAs */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl mx-auto mb-8">
            <div className="border border-gray-200 rounded-sm p-5 bg-gray-50/50">
              <div className="flex items-center gap-2 mb-2">
                <span className="bg-yellow-400 text-gray-900 text-[10px] font-black px-2 py-0.5 rounded-sm tracking-wider">{h.new_label.toUpperCase()}</span>
              </div>
              <p className="text-sm text-gray-700 leading-snug">{h.new_desc}</p>
            </div>
            <div className="border border-gray-200 rounded-sm p-5 bg-gray-50/50">
              <div className="flex items-center gap-2 mb-2">
                <span className="bg-gray-900 text-white text-[10px] font-black px-2 py-0.5 rounded-sm tracking-wider">{h.used_label.toUpperCase()}</span>
              </div>
              <p className="text-sm text-gray-700 leading-snug">{h.used_desc}</p>
            </div>
          </div>

          <div className="flex flex-wrap justify-center gap-3">
            <Link
              to="/request"
              data-testid="hangcha-cta-contact"
              className="inline-flex items-center gap-2 bg-yellow-400 text-gray-900 font-bold hover:bg-yellow-500 transition-colors px-6 py-3 rounded-sm"
            >
              <Mail className="w-4 h-4" /> {h.cta}
            </Link>
            <a
              href="tel:5149734754"
              data-testid="hangcha-cta-call"
              className="inline-flex items-center gap-2 bg-white border border-gray-200 text-gray-900 font-medium hover:bg-gray-50 transition-colors px-6 py-3 rounded-sm"
            >
              <Phone className="w-4 h-4" /> {h.cta_call} 514-973-4754
            </a>
          </div>
        </div>
      </section>
      <section className="py-20 md:py-28 bg-gray-50" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 lg:gap-16 items-center">
            <div className="md:col-span-7">
              <h2 className="font-heading text-3xl sm:text-4xl tracking-tight font-bold text-gray-900 mb-6">
                {t.about.title}
              </h2>
              <p className="text-base text-gray-700 leading-relaxed mb-4">
                {t.about.p1}
              </p>
              <p className="text-base text-gray-700 leading-relaxed">
                {t.about.p2}
              </p>

              <div className="mt-10 grid grid-cols-3 gap-6">
                {[
                  { icon: Zap, label: t.about.stat1_label, desc: t.about.stat1_desc },
                  { icon: Package, label: t.about.stat2_label, desc: t.about.stat2_desc },
                  { icon: Clock, label: t.about.stat3_label, desc: t.about.stat3_desc },
                ].map((stat, i) => (
                  <div key={i} className="border-t-2 border-yellow-400 pt-4">
                    <stat.icon className="w-5 h-5 text-yellow-500 mb-2" />
                    <p className="text-xs tracking-[0.2em] uppercase font-bold text-gray-900">
                      {stat.label}
                    </p>
                    <p className="text-sm text-gray-500 mt-1">{stat.desc}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="md:col-span-5">
              <div className="aspect-[3/4] rounded-sm overflow-hidden border border-gray-200">
                <img src={ABOUT_IMG} alt="" className="w-full h-full object-cover" />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
